/**
 * DecidersEditor — TB-2 (AC-DM-04)
 *
 * Hybrid chip editor for the Deciders column in the Decision Making matrix.
 *
 * Edit mode:
 *  - Existing deciders shown as removable chips (colour-coded by kind)
 *  - Typing in the input filters org Actors (kind='actor') and Role Contracts
 *    (kind='role') for autocomplete suggestions
 *  - Selecting a suggestion adds a structured chip (kind + ref + label)
 *  - Free text + Enter adds a custom chip (kind='custom', label=text)
 *  - Empty/whitespace trimmed; duplicates by label are not added
 *
 * View mode (canManage=false):
 *  - Chips rendered read-only; no input, no remove button
 *
 * Autocomplete sources (⚠ FLAG: no clean org-Roles hook exists separate from
 * Role Contracts — using useRorosRoleContracts() as the "Roles" source.
 * Role Contract name is used as label, id as ref.  If a proper roles list is
 * introduced later, swap out here only.)
 *
 * PRD §6.2, AC-DM-04.
 */

import React, { useMemo, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { t } from '@/lib/i18n';
import { Button } from '@/components/ui/Button';
import { useRorosActors } from '@/hooks/roros/actors';
import { useRorosRoleContracts } from '@/hooks/roros/role-contracts';
import type { RorosActorDTO } from '@/types/roros/actors';
import type { Decider } from '@/types/roros/decisions-shared';

/** Minimal shape for a role-contract entry used by the autocomplete */
interface RoleContractEntry {
  id: string;
  name: string;
  status: string;
}

// ─── Suggestion shape ────────────────────────────────────────────────────────

interface Suggestion {
  kind: 'role' | 'actor';
  ref: string;
  label: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function deciderTitle(d: Decider): string | undefined {
  if (d.kind === 'role' || d.kind === 'actor') {
    return `${d.kind}: ${d.ref}`;
  }
  return undefined;
}

/** Stable chip key using kind + ref (or label for custom) — avoids index keys (L-1) */
function chipKey(d: Decider): string {
  if (d.kind === 'role' || d.kind === 'actor') return `${d.kind}-${d.ref}`;
  return `custom-${d.label}`;
}

// ─── Chip colours per kind ────────────────────────────────────────────────────

const CHIP_CLASSES: Record<Decider['kind'], string> = {
  role:   'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200',
  actor:  'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-200',
  custom: 'bg-muted text-foreground',
};

// ─── Props ────────────────────────────────────────────────────────────────────

interface DecidersEditorProps {
  value: Decider[];
  onChange?: (next: Decider[]) => void;
  canManage: boolean;
  levelLabel: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function DecidersEditor({ value, onChange, canManage, levelLabel }: DecidersEditorProps) {
  const [inputText, setInputText] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [highlightedIdx, setHighlightedIdx] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // ── Data sources for autocomplete ──────────────────────────────────────────
  const actorsQuery = useRorosActors();
  const roleContractsQuery = useRorosRoleContracts();

  // Build a flat suggestions list from actors + role contracts.
  // AC-DMUI-2: With empty input, return full active list (up to 50, excluding
  // already-selected labels) so the dropdown appears immediately on focus.
  // With non-empty input, filter as before.
  const suggestions = useMemo<Suggestion[]>(() => {
    const q = inputText.trim().toLowerCase();
    const selectedLabels = new Set(value.map((d) => d.label.toLowerCase()));

    const actorSuggestions: Suggestion[] = ((actorsQuery.data as RorosActorDTO[]) ?? [])
      .filter((a) => a.status === 'active' && (q ? a.name?.toLowerCase().includes(q) : true))
      .filter((a) => !selectedLabels.has(a.name?.toLowerCase() ?? ''))
      .map((a) => ({ kind: 'actor' as const, ref: a.id, label: a.name ?? '' }));

    const roleSuggestions: Suggestion[] = ((roleContractsQuery.data as unknown as RoleContractEntry[]) ?? [])
      .filter((r) => r.status === 'active' && (q ? r.name?.toLowerCase().includes(q) : true))
      .filter((r) => !selectedLabels.has(r.name?.toLowerCase() ?? ''))
      .map((r) => ({ kind: 'role' as const, ref: r.id, label: r.name ?? '' }));

    const combined = [...actorSuggestions, ...roleSuggestions];
    return q ? combined.slice(0, 10) : combined.slice(0, 50);
  }, [inputText, actorsQuery.data, roleContractsQuery.data, value]);

  // L-2: determine if autocomplete sources are still loading (show placeholder item)
  // AC-DMUI-2: also show loading indicator when dropdown is open on empty input
  const isLoadingAutocomplete =
    (inputText.trim().length > 0 || dropdownOpen) &&
    (actorsQuery.isPending || roleContractsQuery.isPending);

  // ── Handlers ───────────────────────────────────────────────────────────────

  const addDecider = (decider: Decider, keepOpen = false) => {
    if (!onChange) return;
    const label = decider.label.trim();
    if (!label) return; // reject empty
    // Duplicate guard: same label already present
    if (value.some((d) => d.label.toLowerCase() === label.toLowerCase())) return;
    onChange([...value, decider]);
    setInputText('');
    if (keepOpen) {
      // Keep dropdown open for list-driven adds; effect will re-highlight first remaining suggestion
      setDropdownOpen(true);
      setHighlightedIdx(-1);
    } else {
      setDropdownOpen(false);
      setHighlightedIdx(-1);
    }
    inputRef.current?.focus();
  };

  const removeDecider = (idx: number) => {
    if (!onChange) return;
    const next = value.filter((_, i) => i !== idx);
    onChange(next);
  };

  // AC-DMPW-3: Auto-highlight first suggestion whenever dropdown opens or suggestions change.
  // Never leave highlightedIdx at -1 while suggestions exist.
  React.useEffect(() => {
    if (dropdownOpen && suggestions.length > 0 && (highlightedIdx < 0 || highlightedIdx >= suggestions.length)) {
      setHighlightedIdx(0);
    }
  }, [dropdownOpen, suggestions, highlightedIdx]);

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIdx((prev) => Math.min(prev + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      // AC-DMPW-3: do not go below 0 while suggestions exist
      setHighlightedIdx((prev) => Math.max(prev - 1, suggestions.length > 0 ? 0 : -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (suggestions.length > 0) {
        // AC-DMPW-3: add highlighted suggestion (or first if idx is somehow out of range)
        const idx = highlightedIdx >= 0 && highlightedIdx < suggestions.length ? highlightedIdx : 0;
        addDecider(suggestions[idx], true); // keepOpen: dropdown stays for multi-add
      } else {
        // No suggestions → free-text custom chip (closes dropdown)
        const trimmed = inputText.trim();
        if (trimmed) addDecider({ kind: 'custom', label: trimmed }, false);
      }
    } else if (e.key === 'Escape') {
      setDropdownOpen(false);
      setHighlightedIdx(-1);
    } else if (e.key === 'Backspace' && !inputText && value.length > 0) {
      // Remove last chip on Backspace when input is empty
      removeDecider(value.length - 1);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputText(e.target.value);
    setDropdownOpen(true);
    setHighlightedIdx(-1); // effect will auto-set to 0 if suggestions exist
  };

  // Close dropdown when clicking outside.
  // CAPTURE phase: the check must run BEFORE React re-renders on a dropdown-item
  // mousedown. Otherwise the clicked <li> is detached by the add-driven re-render,
  // making contains() false for an in-component click and wrongly closing the
  // dropdown (mouse-select would never stay open — keyboard was unaffected).
  React.useEffect(() => {
    const handleOutside = (ev: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(ev.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutside, true);
    return () => document.removeEventListener('mousedown', handleOutside, true);
  }, []);

  // ── Render ─────────────────────────────────────────────────────────────────

  // View mode — read-only chips
  if (!canManage) {
    return (
      <div className="flex flex-wrap gap-1" data-testid={`deciders-view-${levelLabel}`}>
        {value.length === 0 ? (
          <span className="text-xs text-muted-foreground">—</span>
        ) : (
          value.map((d) => (
            <span
              key={chipKey(d)}
              className={`inline-block rounded-full px-2 py-0.5 text-xs ${CHIP_CLASSES[d.kind]}`}
              title={deciderTitle(d)}
            >
              {d.label}
            </span>
          ))
        )}
      </div>
    );
  }

  // Edit mode
  return (
    <div ref={containerRef} className="relative" data-testid={`deciders-editor-${levelLabel}`}>
      {/* Chip + input row */}
      <div
        className="flex flex-wrap gap-1 min-h-[38px] px-2 py-1 rounded-md border border-input bg-background focus-within:ring-2 focus-within:ring-ring cursor-text"
        onClick={() => inputRef.current?.focus()}
        role="group"
        aria-label={t('Deciders for {{level}}').replace('{{level}}', levelLabel)}
      >
        {value.map((d, idx) => (
          <span
            key={chipKey(d)}
            className={`inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-xs ${CHIP_CLASSES[d.kind]}`}
            title={deciderTitle(d)}
          >
            {d.label}
            {/* H-1: use boilerplate Button for the chip remove action */}
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={(e) => { e.stopPropagation(); removeDecider(idx); }}
              className="ml-0.5 h-3 w-3 p-0 hover:text-destructive focus:outline-none"
              aria-label={t('Remove {{label}}').replace('{{label}}', d.label)}
            >
              <X className="h-3 w-3" aria-hidden />
            </Button>
          </span>
        ))}
        {/* deliberate override: inline chip-container input, no boilerplate bare variant */}
        <input
          ref={inputRef}
          type="text"
          value={inputText}
          onChange={handleInputChange}
          onKeyDown={handleInputKeyDown}
          onFocus={() => setDropdownOpen(true)}
          placeholder={value.length === 0 ? t('Type to search roles/actors, or add custom…') : ''}
          className="flex-1 min-w-[60px] bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          aria-label={t('Add decider for {{level}}').replace('{{level}}', levelLabel)}
          aria-autocomplete="list"
          aria-expanded={dropdownOpen && (suggestions.length > 0 || isLoadingAutocomplete)}
          maxLength={200}
        />
      </div>

      {/* Dropdown */}
      {dropdownOpen && (suggestions.length > 0 || isLoadingAutocomplete) && (
        <ul
          className="absolute z-50 mt-1 w-full max-h-48 overflow-y-auto rounded-md border border-border bg-popover shadow-md text-sm"
          role="listbox"
        >
          {/* L-2: show Loading… item while autocomplete sources are fetching */}
          {isLoadingAutocomplete && suggestions.length === 0 && (
            <li className="px-3 py-2 text-muted-foreground italic" role="option" aria-selected={false}>
              {t('Loading…')}
            </li>
          )}
          {suggestions.map((s, idx) => (
            <li
              key={`${s.kind}-${s.ref}`}
              role="option"
              aria-selected={idx === highlightedIdx}
              className={`flex items-center gap-2 px-3 py-2 cursor-pointer ${
                idx === highlightedIdx
                  ? 'bg-accent text-accent-foreground'
                  : 'hover:bg-accent hover:text-accent-foreground'
              }`}
              onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); addDecider(s, true); /* AC-DMPW-3: keep open for multi-add */ }}
              onMouseEnter={() => setHighlightedIdx(idx)}
            >
              <span
                className={`rounded-full px-1.5 py-0.5 text-xs ${
                  s.kind === 'role' ? CHIP_CLASSES.role : CHIP_CLASSES.actor
                }`}
              >
                {s.kind}
              </span>
              {s.label}
            </li>
          ))}
        </ul>
      )}

      {/* Helper text */}
      <p className="mt-1 text-xs text-muted-foreground">
        {t('Search org roles/actors, or type free text + Enter for custom entries')}
      </p>
    </div>
  );
}
